import {
  Avatar,
  Badge,
  Checkbox,
  Divider,
  IconButton,
  Icons,
  Input,
  Sheet,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useAtom } from 'jotai';
import { type CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toTaskDueDateQueryParam } from '../../../api/created/tasks.ts';
import { getCurrentUser, useCurrentUser, useUsers } from '../../../api/created/users.ts';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import {
  TaskAssignedToPreset,
  taskAssignedEmployeeIdAtom,
  taskAssignedToPresetAtom,
  taskDueDateFilterAtom,
} from '../../../state/taskSearchAtoms.ts';
import { getAvatarColor, getInitials, getUserLabel } from '../../../utils/user.ts';

const PANEL_WIDTH = 320;
const PANEL_GAP = 4;

export function TaskFilterMenu() {
  const [dueDate, setDueDate] = useAtom(taskDueDateFilterAtom);
  const [assignedEmployeeId, setAssignedEmployeeId] = useAtom(taskAssignedEmployeeIdAtom);
  const [preset, setPreset] = useAtom(taskAssignedToPresetAtom);
  const [isOpen, setIsOpen] = useState(false);
  const [isEmployeeSectionExpanded, setIsEmployeeSectionExpanded] = useState(true);
  const [search, setSearch] = useState('');
  const [tempDueDate, setTempDueDate] = useState(dueDate);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: users } = useUsers();
  const { data: currentUser } = useCurrentUser({ enabled: isOpen });

  const sortedUsers = useMemo(() => {
    if (!users) return [];
    return [...users].sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b)));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) return sortedUsers;
    return sortedUsers.filter((user) => getUserLabel(user).toLowerCase().includes(trimmed));
  }, [sortedUsers, search]);

  const hasActiveFilter = !!toTaskDueDateQueryParam(dueDate) || !!assignedEmployeeId;

  useEffect(() => {
    setTempDueDate(dueDate);
  }, [dueDate]);

  const commitDueDate = (value: string) => {
    const apiDueDate = toTaskDueDateQueryParam(value) ?? '';
    setDueDate(apiDueDate);
    if (apiDueDate) {
      setAssignedEmployeeId(null);
      setPreset(TaskAssignedToPreset.ALL);
    }
  };

  const selectPreset = async (nextPreset: TaskAssignedToPreset) => {
    setPreset(nextPreset);
    if (nextPreset !== TaskAssignedToPreset.SELF) {
      setAssignedEmployeeId(null);
      return;
    }

    const user = currentUser ?? (await getCurrentUser());
    setAssignedEmployeeId(user.id);
    setDueDate('');
  };

  const updatePanelPosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + PANEL_GAP,
      left: Math.max(8, rect.right - PANEL_WIDTH),
      zIndex: 1500,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelStyle(undefined);
      return;
    }

    updatePanelPosition();
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }
      if (!document.getElementById('root')?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const selectEmployee = (userId: string) => {
    const nextId = assignedEmployeeId === userId ? null : userId;
    setAssignedEmployeeId(nextId);
    if (nextId) {
      setDueDate('');
      setPreset(userId === currentUser?.id ? TaskAssignedToPreset.SELF : TaskAssignedToPreset.SPECIFIC);
    } else {
      setPreset(TaskAssignedToPreset.ALL);
    }
  };

  const panel =
    isOpen && panelStyle
      ? createPortal(
          <Sheet
            ref={panelRef}
            variant="outlined"
            sx={{
              width: PANEL_WIDTH,
              p: 1,
              borderRadius: 'sm',
              boxShadow: 'md',
              ...panelStyle,
            }}>
            <Stack gap={1} sx={{ px: 1, py: 0.5 }}>
              <Typography level="title-md">Fälligkeitsdatum</Typography>
              <GermanDateInput
                dateInputMode={'calendar'}
                value={tempDueDate}
                onChange={setTempDueDate}
                onValueBlur={commitDueDate}
                showClearButton
              />

              <Divider />

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setIsEmployeeSectionExpanded((prev) => !prev)}>
                <Typography level="title-md">Mitarbeiter</Typography>
                {isEmployeeSectionExpanded ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
              </Stack>

              {isEmployeeSectionExpanded && (
                <Stack gap={1} sx={{ pt: 0.5 }}>
                  <Input
                    size="sm"
                    placeholder="Mitarbeiter suchen"
                    startDecorator={<Icons.Search />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />

                  <Stack gap={0.5}>
                    <Checkbox
                      size="sm"
                      label="Alle Mitarbeiter"
                      checked={preset === TaskAssignedToPreset.ALL}
                      onChange={() => void selectPreset(TaskAssignedToPreset.ALL)}
                    />
                    <Checkbox
                      size="sm"
                      label="Mir zugewiesen"
                      checked={preset === TaskAssignedToPreset.SELF}
                      onChange={() => void selectPreset(TaskAssignedToPreset.SELF)}
                    />
                    <Checkbox
                      size="sm"
                      label="Keine"
                      checked={preset === TaskAssignedToPreset.NONE}
                      onChange={() => void selectPreset(TaskAssignedToPreset.NONE)}
                    />
                  </Stack>

                  <Divider />

                  <Stack gap={0.5} sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                    {filteredUsers.map((user) => (
                      <Stack
                        key={user.id}
                        direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{ cursor: 'pointer', py: 0.25 }}
                        onClick={() => selectEmployee(user.id)}>
                        <Checkbox
                          size="sm"
                          checked={assignedEmployeeId === user.id}
                          onChange={() => selectEmployee(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Avatar size="sm" color={getAvatarColor(user.id)}>
                          {getInitials(user)}
                        </Avatar>
                        <Typography level="body-sm" sx={{ flex: 1 }}>
                          {getUserLabel(user)}
                        </Typography>
                      </Stack>
                    ))}
                    {filteredUsers.length === 0 && (
                      <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 1 }}>
                        Keine Mitarbeiter gefunden
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Sheet>,
          document.body,
        )
      : null;

  return (
    <>
      <Badge invisible={!hasActiveFilter}>
        <IconButton
          ref={buttonRef}
          variant="soft"
          color="neutral"
          onClick={() => setIsOpen((prevState) => !prevState)}>
          <Icons.FilterList />
        </IconButton>
      </Badge>
      {panel}
    </>
  );
}
